import type {
  FieldType,
  RangedFieldTypes,
  ResultRecord,
} from '@dynatrace-sdk/client-query';
import {
  units,
  type ConvertibleUnit,
  type FormatOptions,
  type Unit,
} from '@dynatrace-sdk/units';

import type { DataTableV2ColumnDef } from '../tables/DataTableV2/public.api.js';

function extractColumnTypes(
  rangedFieldTypes: RangedFieldTypes[],
): Map<string, Set<FieldType['type']>> {
  const columnTypes = new Map<string, Set<FieldType['type']>>();
  for (const { mappings } of rangedFieldTypes) {
    for (const key in mappings) {
      const type = mappings[key]?.type ?? 'string';
      if (columnTypes.has(key)) {
        columnTypes.get(key)?.add(type);
      } else {
        columnTypes.set(key, new Set([type]));
      }
    }
  }
  return columnTypes;
}

/**
 * Mapping from a Grail type to a built-in column type of the DataTableV2
 */
const FieldTypeTypeToColumnType = new Map<
  FieldType['type'],
  'date' | 'number' | 'long' | undefined
>([
  ['timestamp', 'date'],
  ['long', 'long'],
  ['double', 'number'],
  ['duration', 'number'],
]);

/**
 * Mapping from a Grail type to a built-in predefined formatter.
 */
const FieldTypeTypeToFormatter = new Map<
  FieldType['type'],
  FormatOptions<Unit, ConvertibleUnit>
>([
  [
    'duration',
    {
      input: units.time.nanosecond,
      cascade: 3,
    },
  ],
]);

function isTimeFrameEntry(value: unknown): value is { start: Date; end: Date } {
  return (
    value !== undefined &&
    value !== null &&
    (value as { start: unknown }).start !== undefined &&
    (value as { start: unknown }).start !== null &&
    (value as { start: unknown }).start instanceof Date &&
    (value as { end: unknown }).end !== undefined &&
    (value as { end: unknown }).end !== null &&
    (value as { end: unknown }).end instanceof Date
  );
}

function isTimeGeoPointEntry(
  value: unknown,
): value is { latitude: number; longitude: number } {
  return (
    value !== undefined &&
    value !== null &&
    (value as { latitude: unknown }).latitude !== undefined &&
    (value as { latitude: unknown }).latitude !== null &&
    typeof (value as { latitude: unknown }).latitude === 'number' &&
    (value as { longitude: unknown }).longitude !== undefined &&
    (value as { longitude: unknown }).longitude !== null &&
    typeof (value as { longitude: unknown }).longitude === 'number'
  );
}

function getTimeframeColumn(
  columnKey: string,
): DataTableV2ColumnDef<ResultRecord | null> {
  // For timeframes, we can split it into two columns for start and end.
  return {
    header: columnKey,
    id: columnKey,
    columns: [
      {
        header: 'start',
        id: `${columnKey}.start`,
        accessor: (row) => {
          if (isTimeFrameEntry(row?.[columnKey])) {
            return row[columnKey].start;
          }
          return '';
        },
        columnType: 'date',
        width: 'content',
      },
      {
        header: 'end',
        id: `${columnKey}.end`,
        accessor: (row) => {
          if (isTimeFrameEntry(row?.[columnKey])) {
            return row[columnKey].end;
          }
          return '';
        },
        columnType: 'date',
        width: 'content',
      },
    ],
  };
}

function getGeoPointColumn(
  columnKey: string,
): DataTableV2ColumnDef<ResultRecord | null> {
  // For geopoints, we can split it into two columns for longitude and latitude.
  return {
    header: columnKey,
    id: columnKey,
    columns: [
      {
        header: 'longitude',
        id: `${columnKey}.longitude`,
        accessor: (row) => {
          if (isTimeGeoPointEntry(row?.[columnKey])) {
            return row[columnKey].longitude;
          }
          return '';
        },
        columnType: 'text',
        width: 'content',
      },
      {
        header: 'latitude',
        id: `${columnKey}.latitude`,
        accessor: (row) => {
          if (isTimeGeoPointEntry(row?.[columnKey])) {
            return row[columnKey].latitude;
          }
          return '';
        },
        columnType: 'text',
        width: 'content',
      },
    ],
  };
}

/**
 * @public
 */
export function convertToColumnsV2(
  rangedFieldTypes: RangedFieldTypes[],
): DataTableV2ColumnDef<ResultRecord | null>[] {
  const extractedTypes = extractColumnTypes(rangedFieldTypes);
  const columns: DataTableV2ColumnDef<ResultRecord | null>[] = [];

  for (const [columnKey, columnTypes] of extractedTypes.entries()) {
    const columnHasOnlyOneType = columnTypes.size === 1;
    const columnType = columnHasOnlyOneType
      ? Array.from(columnTypes)[0]
      : 'string'; // ambiguous definition, fall back to text

    if (columnType === 'timeframe') {
      columns.push(getTimeframeColumn(columnKey));
    } else if (columnType === 'geo_point') {
      columns.push(getGeoPointColumn(columnKey));
    } else {
      columns.push({
        header: columnKey,
        id: columnKey,
        accessor: (row) => row?.[columnKey],
        width: 'content',
        columnType: FieldTypeTypeToColumnType.get(columnType) ?? 'text',
        formatter: FieldTypeTypeToFormatter.get(columnType) ?? undefined,
      });
    }
  }
  return columns;
}
